// Aignoz 10 Sources of Billion-Dollar Problems — /sources (Phase 3)
// Each card generates 10 concrete healthcare problems (24h cached). Click a row to:
//   • Promote → kick off full 12-agent /api/innovate run
//   • Send to Matrix → prefill /matrix with the problem context
//   • Discard → remove from local view
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Compass, EyeOff, Clock, AlertTriangle, Layers, Hourglass, Network,
  CpuIcon, Telescope, FlaskConical, HeartPulse, Plug, Sparkles, Loader2, Rocket,
  Atom, X, ChevronRight, RefreshCw, Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { API } from "@/lib";

const MAGENTA = "#D17EC0";
const LS_MATRIX_PREFILL = "aignoz.matrix.prefill";

// Mirror /app/backend/constants.py (TEN_PROBLEM_SOURCES + SOURCES_QUESTIONS).
const SOURCES = [
  { key: "invisible_biology",            label: "Invisible Biology",            question: "What biological process cannot currently be measured?",         icon: EyeOff,        color: "#A78BFA" },
  { key: "delayed_diagnosis",            label: "Delayed Diagnosis",            question: "What diseases are discovered too late?",                        icon: Clock,         color: "#22D3EE" },
  { key: "therapeutic_failure",          label: "Therapeutic Failure",          question: "Why do treatments fail?",                                       icon: AlertTriangle, color: "#F87171" },
  { key: "biological_heterogeneity",     label: "Biological Heterogeneity",     question: "Why do two patients with the same diagnosis behave differently?", icon: Layers,       color: "#34D399" },
  { key: "clinical_workflow_bottlenecks",label: "Clinical Workflow Bottlenecks",question: "What consumes the most clinician time?",                         icon: Hourglass,     color: "#FBBF24" },
  { key: "data_fragmentation",           label: "Data Fragmentation",           question: "What critical information is disconnected?",                    icon: Network,       color: "#60A5FA" },
  { key: "simulation_deficit",           label: "Simulation Deficit",           question: "What cannot currently be simulated?",                           icon: Telescope,     color: "#F472B6" },
  { key: "rare_disease_neglect",         label: "Rare Disease Neglect",         question: "What diseases lack commercial attention?",                      icon: FlaskConical,  color: "#FB923C" },
  { key: "aging",                        label: "Aging",                        question: "What biological processes drive aging?",                        icon: HeartPulse,    color: MAGENTA   },
  { key: "human_machine_interfaces",     label: "Human-Machine Interfaces",     question: "What biological systems remain inaccessible?",                  icon: Plug,          color: "#5EEAD4" },
];

// ===========================================================================
// PAGE
// ===========================================================================
export default function SourcesPage() {
  const [openKey, setOpenKey] = useState(null);
  const [sets, setSets] = useState({});            // source_key -> SourceProblemSet
  const [busy, setBusy] = useState({});            // source_key -> bool
  const [confirmRefresh, setConfirmRefresh] = useState(null); // source_key to confirm

  async function generate(key, { force = false } = {}) {
    setBusy((b) => ({ ...b, [key]: true }));
    try {
      const r = await fetch(`${API}/sources/${key}/problems${force ? "?force_refresh=true" : ""}`, {
        method: "POST",
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${r.status}`);
      }
      const j = await r.json();
      setSets((s) => ({ ...s, [key]: j }));
      toast.success(`${j.problems?.length || 0} problems ready`, {
        description: j.cached ? "From 24h cache" : "Freshly generated",
      });
      setOpenKey(key);
    } catch (e) {
      toast.error("Generation failed", { description: String(e.message || e) });
    } finally {
      setBusy((b) => ({ ...b, [key]: false }));
    }
  }

  // On modal open, if we don't have data yet, try GET first (cache hit, no LLM cost).
  async function ensureLoaded(key) {
    if (sets[key]) return;
    try {
      const r = await fetch(`${API}/sources/${key}/problems`);
      if (r.ok) {
        const j = await r.json();
        setSets((s) => ({ ...s, [key]: j }));
      }
    } catch { /* silent */ }
  }

  return (
    <div className="h-full overflow-y-auto" data-testid="sources-page">
      <div className="max-w-[1280px] mx-auto px-8 py-10">
        {/* ============================ HEADER ============================ */}
        <header className="mb-10">
          <div className="flex items-center gap-2 mb-2">
            <span
              className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full border text-[10px] uppercase tracking-[0.18em] font-medium"
              style={{ background: "rgba(209,126,192,0.10)", borderColor: "rgba(209,126,192,0.45)", color: "#F0B6DD" }}
            >
              <Compass className="w-3 h-3" /> 10 Sources™
            </span>
            <span className="text-[10px] text-white/40 mono uppercase tracking-[0.16em]">Phase 3 · billion-dollar problem mining</span>
          </div>
          <h1 className="text-4xl font-semibold tracking-tight leading-tight">
            Every healthcare venture begins with a problem worth solving.
          </h1>
          <p className="text-base text-white/55 italic mt-2 max-w-2xl leading-snug">
            Aignoz scans the universe of medicine through ten lenses — each a category of structurally
            unsolved, billion-dollar problems. Pick a lens; we surface ten concrete candidates.
          </p>
        </header>

        {/* ============================ SOURCE GRID ============================ */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {SOURCES.map((s, i) => {
            const have = sets[s.key];
            const isBusy = !!busy[s.key];
            return (
              <motion.div
                key={s.key}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: i * 0.04 }}
                data-testid={`sources-card-${s.key}`}
                className="group rounded-xl border border-white/[0.08] bg-white/[0.012] p-5 flex flex-col hover:border-white/[0.15] transition"
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg border flex items-center justify-center shrink-0"
                       style={{ borderColor: `${s.color}55`, background: `${s.color}14` }}>
                    <s.icon className="w-5 h-5" style={{ color: s.color }} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold tracking-tight text-white/95">{s.label}</div>
                    <div className="text-[10px] mono text-white/40 mt-0.5">Source · {String(i + 1).padStart(2, "0")} / 10</div>
                  </div>
                </div>
                <div className="text-[12px] italic text-white/65 leading-relaxed flex-1 mb-4">
                  &ldquo;{s.question}&rdquo;
                </div>
                <div className="flex items-center justify-between">
                  {have ? (
                    <Badge variant="outline" className="text-[10px] uppercase tracking-[0.12em]"
                      style={{ borderColor: `${s.color}45`, color: s.color }}>
                      {have.problems?.length || 0} ready{have.cached ? " · cached" : ""}
                    </Badge>
                  ) : (
                    <span className="text-[10px] mono text-white/40">no problems generated</span>
                  )}
                  <Button
                    onClick={() => generate(s.key)}
                    disabled={isBusy}
                    size="sm"
                    data-testid={`sources-generate-btn-${s.key}`}
                    className="gap-1.5 bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.12] text-white/90"
                  >
                    {isBusy
                      ? <Loader2 className="w-3 h-3 animate-spin" />
                      : <Sparkles className="w-3 h-3" />}
                    {have ? "Open Problems" : "Generate 10 Problems"}
                  </Button>
                </div>
                {have && (
                  <button
                    onClick={() => { ensureLoaded(s.key); setOpenKey(s.key); }}
                    className="mt-2 text-[10px] uppercase tracking-[0.14em] text-teal-300/70 hover:text-teal-200 inline-flex items-center gap-1 self-end"
                    data-testid={`sources-open-modal-${s.key}`}
                  >
                    Open <ChevronRight className="w-3 h-3" />
                  </button>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="mt-12 text-center text-[10px] text-white/30 leading-relaxed">
          24h Mongo cache · ~₹5 / generation · Designed by{" "}
          <span className="text-white/55">Dr. Sriinidhi BV (MBBS, MD)</span> · Aignoz Innovation OS™
        </div>
      </div>

      {/* ============================ MODAL ============================ */}
      <ProblemsModal
        openKey={openKey}
        source={SOURCES.find((s) => s.key === openKey)}
        set={openKey ? sets[openKey] : null}
        onClose={() => setOpenKey(null)}
        onRefreshClick={() => setConfirmRefresh(openKey)}
        onDiscard={(idx) => {
          setSets((s) => {
            const cur = s[openKey];
            if (!cur) return s;
            const problems = (cur.problems || []).filter((_, i) => i !== idx);
            return { ...s, [openKey]: { ...cur, problems } };
          });
        }}
      />

      {/* ============================ REFRESH CONFIRM ============================ */}
      <Dialog open={!!confirmRefresh} onOpenChange={(o) => !o && setConfirmRefresh(null)}>
        <DialogContent className="max-w-md" data-testid="sources-refresh-confirm-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-amber-300" /> Refresh problems?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-white/75 leading-relaxed">
            <p>
              This will discard the current 24h cache for this source and trigger a new GPT-5 call.
            </p>
            <div className="rounded-md border border-amber-400/30 bg-amber-500/[0.06] p-3">
              <div className="text-[10px] uppercase tracking-[0.14em] text-amber-200 mb-1">Estimated cost</div>
              <div className="text-2xl mono font-semibold text-amber-200">₹5</div>
              <div className="text-[10px] text-white/55 mt-1">1 LLM call · ~25-40 seconds</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmRefresh(null)}>Cancel</Button>
            <Button
              onClick={() => {
                const k = confirmRefresh;
                setConfirmRefresh(null);
                if (k) generate(k, { force: true });
              }}
              className="bg-amber-500 hover:bg-amber-400 text-black gap-2"
              data-testid="sources-refresh-confirm-btn"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===========================================================================
// PROBLEMS MODAL — 10 numbered rows, per-row actions
// ===========================================================================
function ProblemsModal({ openKey, source, set, onClose, onRefreshClick, onDiscard }) {
  if (!source) return null;
  const problems = set?.problems || [];
  return (
    <Dialog open={!!openKey} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-3xl max-h-[88vh] overflow-y-auto"
        data-testid="sources-problems-modal"
      >
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg border flex items-center justify-center shrink-0"
                 style={{ borderColor: `${source.color}55`, background: `${source.color}14` }}>
              <source.icon className="w-5 h-5" style={{ color: source.color }} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-[0.18em] text-teal-300/80 mb-0.5">Source</div>
              <DialogTitle className="text-lg leading-snug">{source.label}</DialogTitle>
              <div className="text-[12px] italic text-white/55 mt-1">&ldquo;{source.question}&rdquo;</div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={onRefreshClick}
              className="gap-1.5 shrink-0"
              data-testid="sources-refresh-btn"
            >
              <RefreshCw className="w-3 h-3" /> Refresh
            </Button>
          </div>
          {set?.cached && (
            <div className="mt-2 text-[10px] text-white/45 mono">
              cached at {set.generated_at?.slice(0, 19)}
            </div>
          )}
        </DialogHeader>

        <div className="space-y-2 mt-3">
          {problems.length === 0 && (
            <div className="text-center text-sm text-white/40 italic py-10">No problems available.</div>
          )}
          {problems.map((p, idx) => (
            <ProblemRow
              key={p.id}
              idx={idx}
              problem={p}
              source={source}
              onDiscard={() => onDiscard(idx)}
              onClose={onClose}
            />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ProblemRow({ idx, problem, source, onDiscard, onClose }) {
  const navigate = useNavigate();
  const [promoteBusy, setPromoteBusy] = useState(false);

  async function promote() {
    setPromoteBusy(true);
    try {
      const r = await fetch(`${API}/sources/problems/${problem.id}/promote`, { method: "POST" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      toast.success("Promoted to 12-agent venture", { description: "Opening workspace…" });
      setTimeout(() => navigate(`/projects/${j.bioforge_run_id}/overview`), 500);
      onClose?.();
    } catch (e) {
      toast.error("Promotion failed", { description: String(e.message || e) });
    } finally { setPromoteBusy(false); }
  }

  function sendToMatrix() {
    try {
      localStorage.setItem(LS_MATRIX_PREFILL, JSON.stringify({
        text: problem.text,
        biology_hint: problem.biology_hint,
        tech_hint: problem.tech_hint,
        source: source.label,
        problem_id: problem.id,
      }));
      toast("Sent to Matrix", { description: "Pick the 3 axes on /matrix." });
      navigate("/matrix");
      onClose?.();
    } catch (e) {
      toast.error("Send to Matrix failed", { description: String(e.message || e) });
    }
  }

  return (
    <div
      data-testid={`sources-problem-row-${idx}`}
      className="rounded-lg border border-white/[0.06] bg-white/[0.012] p-3"
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-6 h-6 rounded-full border border-white/[0.12] mono text-[11px] flex items-center justify-center text-white/65">
          {idx + 1}
        </div>
        <div className="flex-1 min-w-0">
          <div
            data-testid={`sources-problem-text-${idx}`}
            className="text-[13px] text-white/90 leading-snug"
          >
            {problem.text}
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5 text-[10px] text-white/55">
            {problem.biology_hint && (
              <span><span className="text-emerald-300/70 mono uppercase tracking-[0.1em]">bio</span> {problem.biology_hint}</span>
            )}
            {problem.tech_hint && (
              <span><span className="text-cyan-300/70 mono uppercase tracking-[0.1em]">tech</span> {problem.tech_hint}</span>
            )}
          </div>
        </div>
      </div>
      <div className="flex gap-1.5 mt-3 ml-9">
        <Button
          onClick={promote}
          disabled={promoteBusy}
          size="sm"
          data-testid={`sources-promote-${idx}`}
          className="h-7 px-2.5 text-[10px] uppercase tracking-[0.14em] gap-1.5 bg-teal-500 hover:bg-teal-400 text-black font-medium"
        >
          {promoteBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Rocket className="w-3 h-3" />}
          Promote
        </Button>
        <Button
          onClick={sendToMatrix}
          size="sm"
          variant="outline"
          data-testid={`sources-send-matrix-${idx}`}
          className="h-7 px-2.5 text-[10px] uppercase tracking-[0.14em] gap-1.5"
        >
          <Atom className="w-3 h-3" /> Send to Matrix
        </Button>
        <Button
          onClick={onDiscard}
          size="sm"
          variant="ghost"
          data-testid={`sources-discard-${idx}`}
          className="h-7 px-2.5 text-[10px] uppercase tracking-[0.14em] gap-1.5 text-white/45 hover:text-rose-300"
        >
          <Trash2 className="w-3 h-3" /> Discard
        </Button>
      </div>
    </div>
  );
}
